import type { ReportSankeyNode } from '@/modules/analysis/stores/types'
import {
  accountKey,
  type FlowOfFunds,
  isOtherBucket,
  roleOf,
} from './flowModel'
import { type NodeTotals, nodeTotals } from './flowTotals'

const nodeSize = (totals: NodeTotals | undefined) =>
  Math.max(totals?.in.amount ?? 0, totals?.out.amount ?? 0)

const isAccount = (node: ReportSankeyNode) =>
  !['payer', 'payee'].includes(roleOf(node))

// Each column runs biggest first, with the "Other" bucket always last.
// Both account columns share one order, so money that stays in an account
// runs straight across.
export function flowNodeOrder(
  flow: FlowOfFunds,
  totals: Map<string, NodeTotals> = nodeTotals(flow),
): Map<string, number> {
  const accountSizes = new Map<string, number>()
  for (const node of flow.nodes.filter(isAccount)) {
    const key = accountKey(node)
    const size = nodeSize(totals.get(node.name))
    accountSizes.set(key, (accountSizes.get(key) ?? 0) + size)
  }
  const sizeOf = (node: ReportSankeyNode) =>
    isAccount(node)
      ? (accountSizes.get(accountKey(node)) ?? 0)
      : nodeSize(totals.get(node.name))

  const ordered = [...flow.nodes].sort(
    (first, second) =>
      Number(isOtherBucket(first)) - Number(isOtherBucket(second)) ||
      sizeOf(second) - sizeOf(first) ||
      accountKey(first).localeCompare(accountKey(second)),
  )
  return new Map(ordered.map((node, position) => [node.name, position]))
}
